import React from 'react';
import * as Icons from '../icons';
import {
  getCustomIcon,
  hasCustomIcon,
  resolveIconColor,
  CustomIconConfig,
} from '../../config/icon-registry';

interface IconProps {
  icon: string;
  size?: number;
  strokeWidth?: number;
  primaryColor?: string;
  secondaryColor?: string;
  className?: string;
  title?: string;
}

/**
 * Icon component for React
 *
 * Renders icons from:
 * 1. Built-in icon components (defined in ../icons)
 * 2. Custom registered icons (registered via registerIcon)
 */
export const Icon: React.FC<IconProps> = ({
  icon,
  title,
  size = 24,
  strokeWidth = 2,
  primaryColor = 'currentColor',
  secondaryColor = 'currentColor',
  className,
}) => {
  // Check built-in icons first
  // Map kebab-case icon name to camelCase (e.g., 'zoom-in' -> 'zoomIn')
  const iconKey = icon.replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase());
  
  // Get the component from the Icons registry
  const BuiltInIcon = (Icons as any)?.icons?.[iconKey];
  
  if (BuiltInIcon) {
    return (
      <BuiltInIcon
        size={size}
        strokeWidth={strokeWidth}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        className={className}
        title={title}
      />
    );
  }

  // Check custom registered icons
  if (hasCustomIcon(icon)) {
    const config = getCustomIcon(icon)!;
    return renderCustomIcon(config, {
      size,
      strokeWidth,
      primaryColor,
      secondaryColor,
      className,
      title,
    });
  }

  // Icon not found
  console.warn(`Icon not found: ${icon}`);
  return null;
};

/**
 * Renders a custom icon from its configuration
 */
function renderCustomIcon(
  config: CustomIconConfig,
  props: {
    size: number;
    strokeWidth: number;
    primaryColor: string;
    secondaryColor: string;
    className?: string;
    title?: string;
  },
): React.ReactElement {
  const { size, strokeWidth, primaryColor, secondaryColor, className, title } = props;
  const defaultStrokeWidth = config.strokeWidth ?? strokeWidth;

  return (
    <svg
      width={size}
      height={size}
      viewBox={config.viewBox || '0 0 24 24'}
      strokeLinecap={config.strokeLinecap}
      strokeLinejoin={config.strokeLinejoin}
      className={className}
      role="img"
      aria-label={title}
      fill="none"
      stroke="currentColor"
    >
      {config.paths.map((path, i) => {
        const strokeColor = resolveIconColor(path.stroke, primaryColor, secondaryColor);
        const fillColor = resolveIconColor(path.fill, primaryColor, secondaryColor);
        const pathStrokeWidth = path.strokeWidth ?? defaultStrokeWidth;

        return (
          <path
            key={i}
            d={path.d}
            stroke={strokeColor}
            fill={fillColor}
            strokeWidth={strokeColor !== 'none' ? pathStrokeWidth : undefined}
            opacity={path.opacity}
          />
        );
      })}
    </svg>
  );
}
